import React, { useState } from "react";
import styles from "./styles.module.scss";
import theme from "../../Styles/theme.scss";
import LilliSelect from "../LilliDesignSystem/LilliSelect";
import type { LilliSelectOption } from "../LilliDesignSystem/LilliSelect";
import PrimaryButton from "../PrimaryButton";
import DateRangePicker from "../DateRangePicker";
import { PRESETS, PRESET_OPTIONS } from "../../Pages/InstallPerformance/utils";
import type { PresetKey, Granularity } from "../../Pages/InstallPerformance/utils";
import { useViewport } from "../ViewportProvider";
import { mobileBreakpoint } from "../../Services/config";

interface DatePresetSelectorProps {
  preset: PresetKey;
  setPreset: (key: PresetKey) => void;
  granularity: Granularity;
  setGranularity: (g: Granularity) => void;
  customRange: { startDate: Date; endDate: Date } | null;
  setCustomRange: (range: { startDate: Date; endDate: Date }) => void;
}

const GRANULARITY_OPTIONS: { value: Granularity; label: string; short: string }[] = [
  { value: "D", label: "Day", short: "D" },
  { value: "W", label: "Week", short: "W" },
  { value: "M", label: "Month", short: "M" },
];

const DatePresetSelector = ({
  preset,
  setPreset,
  granularity,
  setGranularity,
  customRange,
  setCustomRange,
}: DatePresetSelectorProps) => {
  const { width } = useViewport();
  const isMobile = width < mobileBreakpoint;
  const [showPicker, setShowPicker] = useState(false);

  const selectedOption = PRESET_OPTIONS.find((o) => o.value === preset) || null;

  const handlePresetChange = (option: LilliSelectOption | null) => {
    if (!option) return;
    const key = option.value as PresetKey;
    setPreset(key);
    const presetConfig = PRESETS.find((p) => p.key === key);
    if (presetConfig) {
      setGranularity(presetConfig.defaultGranularity);
    }
    if (key === "custom") {
      setShowPicker(true);
    } else {
      setShowPicker(false);
    }
  };

  const getButtonStyle = (g: Granularity): React.CSSProperties => {
    const isActive = g === granularity;
    return {
      border: `1px solid ${isActive ? theme.primary1 : theme.neutral2}`,
      backgroundColor: isActive ? theme.primary1 : theme.white,
      color: theme.neutral7,
      borderRadius: isMobile ? 3 : 4,
      padding: isMobile ? "4px 6px" : "8px 16px",
      fontWeight: isActive ? 500 : 400,
      minWidth: 35,
    };
  };

  const handleCustomRangeChange = (range: {
    startDate: Date;
    endDate: Date;
    startDateYetToBeSelected?: boolean;
    endDateYetToBeSelected?: boolean;
  }) => {
    setCustomRange({
      startDate: range.startDate,
      endDate: range.endDate,
    });
  };

  const dateRangeValue = customRange
    ? {
        startDate: customRange.startDate,
        endDate: customRange.endDate,
        startDateYetToBeSelected: false,
        endDateYetToBeSelected: false,
      }
    : {
        startDate: new Date(),
        endDate: new Date(),
        startDateYetToBeSelected: true,
        endDateYetToBeSelected: true,
      };

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <div className={styles.preset_select}>
          <LilliSelect
            options={PRESET_OPTIONS}
            value={selectedOption}
            onChange={handlePresetChange}
            placeholder="Select time range"
            name="preset-selector"
          />
        </div>
        <div className={styles.granularity}>
          {GRANULARITY_OPTIONS.map((g) => (
            <PrimaryButton
              key={g.value}
              style={getButtonStyle(g.value)}
              onClick={() => setGranularity(g.value)}
            >
              {isMobile ? g.short : g.label}
            </PrimaryButton>
          ))}
        </div>
      </div>
      {preset === "custom" && (
        <div className={styles.picker_container}>
          <DateRangePicker
            dateRange={dateRangeValue}
            setDateRange={handleCustomRangeChange}
            showPicker={showPicker}
            setShowPicker={setShowPicker}
            minDate={new Date(2020, 0, 1)}
            maxDate={new Date()}
            highlightRequired={false}
          />
        </div>
      )}
    </div>
  );
};

export default DatePresetSelector;
